
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Pencil } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Provider } from "@/hooks/useProviders";
import { ImageUpload } from "@/components/ImageUpload";

const providerSchema = z.object({
  name: z.string().min(1, "Provider name is required"),
  description: z.string().optional(),
  location: z.string().min(1, "Location is required"),
  address: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email("Invalid email address").optional().or(z.literal("")),
  website: z.string().url("Invalid URL").optional().or(z.literal("")),
  provider_contact_email: z.string().email("Invalid email address").optional().or(z.literal("")),
  email_notifications_enabled: z.boolean(),
  blur_customer_emails: z.boolean(),
  is_hidden: z.boolean(),
  specialties: z.string().optional(),
  image_url: z.string().optional(),
  commission_fee_percent: z.string().optional(),
  check_in_window_minutes: z.number().min(1, "Must be at least 1 minute").max(120, "Cannot exceed 120 minutes"),
  custom_domain: z.string().optional(),
});

type ProviderFormData = z.infer<typeof providerSchema>;

interface EditProviderDialogProps {
  provider: Provider;
}

export const EditProviderDialog = ({ provider }: EditProviderDialogProps) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<ProviderFormData>({
    resolver: zodResolver(providerSchema),
    defaultValues: {
      name: provider.name || "",
      description: provider.description || "",
      location: provider.location || "",
      address: provider.address || "",
      phone: provider.phone || "",
      email: provider.email || "",
      website: provider.website || "",
      provider_contact_email: provider.provider_contact_email || "",
      email_notifications_enabled: provider.email_notifications_enabled !== false,
      blur_customer_emails: (provider as any).blur_customer_emails !== false,
      is_hidden: (provider as any).is_hidden || false,
      specialties: provider.specialties?.join(", ") || "",
      image_url: provider.image_url || "",
      commission_fee_percent: provider.commission_fee_percent?.toString() || "2.90",
      check_in_window_minutes: (provider as any).check_in_window_minutes || 5,
      custom_domain: (provider as any).custom_domain || "",
    },
  });

  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const onSubmit = async (data: ProviderFormData) => {
    setIsLoading(true);
    console.log("Updating provider with data:", data);

    try {
      const slug = generateSlug(data.name);
      
      // Convert specialties string to array
      const specialtiesArray = data.specialties 
        ? data.specialties.split(',').map(s => s.trim()).filter(s => s.length > 0)
        : [];

      // Parse commission fee percentage
      const commissionFeePercent = data.commission_fee_percent 
        ? parseFloat(data.commission_fee_percent) 
        : null;

      const providerData = {
        name: data.name,
        description: data.description || null,
        location: data.location,
        address: data.address || null,
        phone: data.phone || null,
        email: data.email || null,
        website: data.website || null,
        provider_contact_email: data.provider_contact_email || null,
        email_notifications_enabled: data.email_notifications_enabled,
        blur_customer_emails: data.blur_customer_emails,
        is_hidden: data.is_hidden,
        specialties: specialtiesArray,
        image_url: data.image_url || null,
        slug: slug,
        commission_fee_percent: commissionFeePercent,
        check_in_window_minutes: data.check_in_window_minutes,
        custom_domain: data.custom_domain || null,
      };

      console.log("Updating provider data:", providerData);

      const { data: updatedProvider, error } = await supabase
        .from('providers')
        .update(providerData)
        .eq('id', provider.id)
        .select()
        .single();

      if (error) {
        console.error("Error updating provider:", error);
        throw error;
      }

      console.log("Provider updated successfully:", updatedProvider);

      toast({
        title: "Success",
        description: "Provider profile updated successfully!",
      });

      // Reset form and close dialog
      setOpen(false);
      
      // Refresh providers data - invalidate both query keys
      queryClient.invalidateQueries({ queryKey: ['providers'] });
      queryClient.invalidateQueries({ queryKey: ['admin-providers'] });

    } catch (error: any) {
      console.error("Error updating provider:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update provider profile",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Pencil className="h-4 w-4 mr-1" />
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Provider Profile</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Provider Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter provider name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Enter provider description" 
                      {...field} 
                      rows={3}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location *</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., London, UK" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter phone number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter full address" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="provider@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="provider_contact_email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Provider Contact Email</FormLabel>
                    <FormControl>
                      <Input placeholder="contact@provider.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email_notifications_enabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Email Notifications</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      Receive email notifications for new bookings
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="blur_customer_emails"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Blur Customer Emails</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      Blur customer email addresses on booking detail pages for privacy
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_hidden"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Hide from Listings</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      Hide this provider from home, auth, and provider switcher. Still accessible via direct URL.
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="website"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Website</FormLabel>
                  <FormControl>
                    <Input placeholder="https://example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="specialties"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Specialties (comma-separated)</FormLabel>
                  <FormControl>
                    <Input placeholder="Yoga, Meditation, Fitness" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="image_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Provider Logo</FormLabel>
                  <FormControl>
                    <ImageUpload
                      bucketName="providers"
                      folder="logos"
                      currentImageUrl={field.value}
                      onImageUploaded={(url) => field.onChange(url)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="commission_fee_percent"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Commission Fee %</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="2.90" 
                      type="number" 
                      step="0.01" 
                      min="0" 
                      max="100" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="check_in_window_minutes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Check-in Window (Minutes)</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      min="1" 
                      max="120" 
                      placeholder="5"
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 5)}
                    />
                  </FormControl>
                  <FormMessage />
                  <p className="text-xs text-muted-foreground">
                    How many minutes before their session can customers check in?
                  </p>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="custom_domain"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Custom Domain</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="my.example.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                  <p className="text-xs text-muted-foreground">
                    Enter the custom domain CNAMEd to wellsuites.co (e.g., my.cold-culture.co.uk). When visitors browse via this domain, they will only see this provider.
                  </p>
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Updating..." : "Update Provider"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
